import { MaxTagSize } from '../../utils/commonConstants';
import TagUtils from '../../utils/tagUtils';
import BorderlessTextField from '../borderlessTextField';
import { forwardRef } from 'react';

export interface IBorderlessTagFieldProps {
	tags?: string[];
	inputValue?: string;
	searchOnly?: boolean;
	placeholder?: string;
	testId?: string;
	onInput: (input: string) => void;
	onTagsAppend?: (tags: string[]) => void;
	clearSelection?: () => void;
	onSameTagInInput?: (tag: string, index: number) => void;
}

const BorderlessTagField: React.ForwardRefExoticComponent<
	IBorderlessTagFieldProps & React.RefAttributes<HTMLInputElement>
> = forwardRef(
	(
		{ tags, inputValue, searchOnly, placeholder, testId, onInput, onTagsAppend, clearSelection, onSameTagInInput },
		ref
	) => {
		const separateButtons = ['Tab', ';', ',', 'Enter'];

		const truncateString = (str: string) => {
			if (str.length > MaxTagSize) {
				str = str.substring(0, MaxTagSize);
			}

			return str;
		};

		return (
			<BorderlessTextField
				placeholder={placeholder}
				value={inputValue}
				inputProps={{ maxLength: MaxTagSize, 'data-test-id': testId }}
				multiline
				onChange={e => {
					clearSelection && clearSelection();
					onInput(TagUtils.cleanup(e.target.value));
				}}
				onPaste={e => {
					const pastedData = e.clipboardData.getData('text/plain');

					if (!pastedData?.length || searchOnly) {
						return;
					}

					const newTags = TagUtils.cleanup(TagUtils.split(pastedData));

					if (tags && newTags?.length > 1) {
						const existingTags = new Set(tags);
						const uniqueTags = newTags
							.map(t => truncateString(t.trim()))
							.filter(t => t.length > 0 && !existingTags.has(t));

						if (uniqueTags.length > 0 && onTagsAppend) {
							onTagsAppend(uniqueTags);
						}

						e.preventDefault();
					}
				}}
				onKeyDown={e => {
					if (!searchOnly && separateButtons.includes(e.key)) {
						e.preventDefault();

						const tag = inputValue.trim();
						truncateString(tag);

						if (tag.length > 0) {
							const tagIndex = tags.findIndex(t => TagUtils.equals(t, tag));

							if (tagIndex >= 0) {
								onSameTagInInput && onSameTagInInput(tag, tagIndex);
							} else if (onTagsAppend) {
								onTagsAppend([tag]);
							}
						}

						onInput('');
					}
				}}
				inputRef={ref}
			/>
		);
	}
);
BorderlessTagField.displayName = 'BorderlessTagField';

export default BorderlessTagField;
